import { normalizeGoidType } from '@/api/types'
import { MRDEntitySelect } from '@/components/Grid/CellEditors'
import type { components } from '@/types/api/v1'
import { BRANDED_OPTIONS } from '@/utils/branded'
import { Form, Input, InputNumber, Select, Switch } from 'antd'
import React from 'react'
import { FormFieldConfig } from '../types'

type ColumnDef = components['schemas']['ColumnDef']

/**
 * Generate form field configurations from backend ColumnDef schemas
 */
export const generateFormFieldConfigs = (
  columnDefs: ColumnDef[],
): FormFieldConfig[] => {
  if (!columnDefs || columnDefs.length === 0) return []

  const skipFields = [
    'id',
    '_id',
    'goid',
    'revision_id',
    'actions',
    'created_at',
    'updated_at',
    'is_active',
  ]

  return columnDefs
    .filter((col) => {
      if (skipFields.includes(col.field)) return false
      if (col.hide) return false
      return true
    })
    .map((col) => {
      const field = col.field
      const label = col.headerName || field

      if (col.goid_type) {
        return {
          field,
          label,
          type: 'entity' as const,
          required: !col.nullable,
          entityType: normalizeGoidType(col.goid_type),
          multi: col.multi,
        }
      }

      if (field === 'branded') {
        return {
          field,
          label,
          type: 'select' as const,
          required: false,
          options: BRANDED_OPTIONS,
        }
      }

      let type: FormFieldConfig['type'] = 'text'

      if (col.type === 'boolean') {
        type = 'boolean'
      } else if (col.type === 'number') {
        type = 'number'
      }

      return {
        field,
        label,
        type,
        required: !col.nullable,
      }
    })
}

/**
 * Generate form field components from field configurations
 */
export const generateFormFields = (
  fieldConfigs: FormFieldConfig[],
): React.ReactNode[] => {
  return fieldConfigs.map((config) => {
    const { field, label, type, required, options } = config

    const rules = required
      ? [
          {
            required: true,
            message: `Please enter ${label.toLowerCase()}`,
          },
        ]
      : undefined

    switch (type) {
      case 'entity':
        return (
          <Form.Item key={field} name={field} label={label} rules={rules}>
            <MRDEntitySelect
              entityType={config.entityType!}
              multi={config.multi}
            />
          </Form.Item>
        )

      case 'boolean':
        return (
          <Form.Item
            key={field}
            name={field}
            label={label}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
        )

      case 'select':
        return (
          <Form.Item key={field} name={field} label={label} rules={rules}>
            <Select>
              {options?.map((value: string) => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )

      case 'number':
        return (
          <Form.Item key={field} name={field} label={label} rules={rules}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'text':
      default:
        return (
          <Form.Item key={field} name={field} label={label} rules={rules}>
            <Input />
          </Form.Item>
        )
    }
  })
}
